import { ReactNode } from "react";
import styled from "styled-components";

import { WidgetFrameContent } from "@/components/layout/WidgetFrame/WidgetFrameContent";
import { Dependency } from "@/components/ui/DataContent/data-content.types";
import { Loader } from "@/components/ui/Loader";
import { Table, TableProps, Row } from "@/components/ui/table-final-saviour/Table";
import { Icon } from "@/components/ui-deprecated";

const MessageContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    color: ${({ theme }) => theme.color.textGrayDairyDarker};
`;

type Props<TRow extends Row> = Omit<TableProps<TRow>, "data"> & {
    dataSource: { data?: TRow[] } & Dependency;
    footer?: ReactNode;
    loadingMessage?: ReactNode;
    noDataMessage?: ReactNode;
    noRowsMessage?: ReactNode;
};

const WidgetFrameTable = <TRow extends Row>(props: Props<TRow>) => {
    const { dataSource, loadingMessage, noDataMessage, noRowsMessage, ...tableProps } = props;
    const { data, isLoading, isFetching, isError } = dataSource;

    if (isLoading || isFetching) {
        return (
            <WidgetFrameContent>
                {loadingMessage ? (
                    loadingMessage
                ) : (
                    <MessageContainer>
                        <Loader size={"24px"} />
                    </MessageContainer>
                )}
            </WidgetFrameContent>
        );
    }

    if (isError) {
        return (
            <WidgetFrameContent>
                <MessageContainer>
                    <Icon icon="exclamation-circle" />
                    Error loading data
                </MessageContainer>
            </WidgetFrameContent>
        );
    }

    if (!data) {
        return (
            <WidgetFrameContent>
                {noDataMessage ? (
                    noDataMessage
                ) : (
                    <MessageContainer>
                        <Icon icon="exclamation-circle" />
                        No data
                    </MessageContainer>
                )}
            </WidgetFrameContent>
        );
    }

    if (data.length === 0) {
        return (
            <WidgetFrameContent>
                {noRowsMessage ? (
                    noRowsMessage
                ) : (
                    <MessageContainer>
                        <Icon icon="exclamation-circle" />
                        No rows found
                    </MessageContainer>
                )}
            </WidgetFrameContent>
        );
    }

    return (
        <>
            <Table<TRow> data={data} {...tableProps} />
            {props.footer}
        </>
    );
};

export { WidgetFrameTable };
export type { Props as WidgetFrameTableProps };
